import { markRaw } from 'vue'

import NoteCard from 'src/components/partials/timeline/cards/Note'
import NoteTile from 'components/partials/timeline/tiles/Note'

import AttachmentCard from 'src/components/partials/timeline/cards/Attachment'
import AttachmentTile from 'components/partials/timeline/tiles/Attachment'

import LogCard from 'src/components/partials/timeline/cards/Log'
import LogTile from 'components/partials/timeline/tiles/Log'

import CommunicationCard from 'src/components/partials/timeline/cards/Communication'
import CommunicationTile from 'components/partials/timeline/tiles/Communication'

import ReportCard from 'src/components/partials/timeline/cards/Report'
import ReportTile from 'components/partials/timeline/tiles/Report'

import InvoiceCard from 'src/components/partials/timeline/cards/Invoice'
import InvoiceTile from 'components/partials/timeline/tiles/Invoice'

import QuotationCard from 'src/components/partials/timeline/cards/Quotation'
import QuotationTile from 'components/partials/timeline/tiles/Quotation'

import DrawingCard from 'src/components/partials/timeline/cards/Drawing'
import DrawingTile from 'components/partials/timeline/tiles/Drawing'

import AppointmentCard from 'src/components/partials/timeline/cards/Appointment'
import AppointmentFutureCard from 'src/components/partials/timeline/cards/AppointmentFuture'
import AppointmentTile from 'components/partials/timeline/tiles/Appointment'
import AppointmentFutureTile from 'components/partials/timeline/tiles/AppointmentFuture'

const permissions = process.env.PERMISSIONS

/**
 * Common methods between Simple and main timeline implementations
 */
export default {
  emits: ['removeNote', 'updateNote', 'pinNote', 'removeAttachment', 'pinAttachment'],
  methods: {
    basePartialTypes (config = {}, permission = null) {
      const getConfigForType = (type) => {
        return config[type] ? config[type] : {}
      }

      return [
        {
          value: 'note',
          label: 'Notes',
          singleLabel: 'Note',
          minHeight: 47,
          component: markRaw(NoteCard),
          permission,
          tile: markRaw(NoteTile),
          props: (partial) => {
            return {
              note: partial,
              ...getConfigForType('note')
            }
          },
          searchHandler: (item, term) => {
            return this.stripHtml(item.value || '').toLowerCase().includes(term)
          },
          removeHandler: (note) => {
            this.$emit('removeNote', note.id, note.timeline_reference)
          },
          saveHandler: (payload) => {
            if (this.stripHtml(payload.value || '').length < 1) { return }

            this.$emit('updateNote', payload, () => {
              payload.is_updated = true
              this.$q.notify({
                color: 'green',
                message: 'Note updated'
              })
            })
          },
          pinHandler: (note, done) => {
            this.$emit('pinNote', note.id, note.timeline_pinned, done, note.timeline_reference)
          }
        },
        {
          value: 'attachment',
          label: 'Files',
          singleLabel: 'File',
          minHeight: 84,
          component: markRaw(AttachmentCard),
          permission,
          tile: markRaw(AttachmentTile),
          props: (partial) => {
            return {
              file: partial,
              ...getConfigForType('attachment')
            }
          },
          searchHandler: (item, term) => {
            return (item.title || '').toLowerCase().includes(term) ||
              (item.file_name || '').toLowerCase().includes(term)
          },
          removeHandler: (file) => {
            this.$emit('removeAttachment', file.id, file.key, file.timeline_reference)
          },
          pinHandler: (file, done) => {
            this.$emit('pinAttachment', file.id, file.timeline_pinned, file.key, done, file.timeline_reference)
          }
        },
        {
          value: 'communication',
          label: 'Communication Log',
          singleLabel: 'Communication',
          component: markRaw(CommunicationCard),
          permission,
          minHeight: 68,
          tile: markRaw(CommunicationTile),
          props: (partial) => {
            return {
              communication: partial,
              ...getConfigForType('communication')
            }
          },
          searchHandler: (item, term) => {
            return (item.complete_msg || '').toLowerCase().includes(term) ||
              (item.error_msg || '').toLowerCase().includes(term) ||
              (item.reference_id || '').toLowerCase().includes(term)
          }
        },
        {
          value: 'log',
          label: 'Activity Log',
          singleLabel: 'Activity',
          component: markRaw(LogCard),
          permission,
          minHeight: 32,
          tile: markRaw(LogTile),
          props: (partial) => {
            return {
              log: partial,
              ...getConfigForType('log')
            }
          },
          searchHandler: (item, term) => {
            return (item.description || '').toLowerCase().includes(term)
          }
        },
        {
          value: 'appointment',
          label: 'Appointments',
          singleLabel: 'Appointment',
          component: markRaw(AppointmentCard),
          permission: permissions.canAccessCalendar,
          minHeight: 76,
          tile: markRaw(AppointmentTile),
          props: (partial) => {
            return {
              appointment: partial,
              ...getConfigForType('appointment')
            }
          },
          searchHandler: (item, term) => {
            return (item.address || '').toLowerCase().includes(term) ||
              (item.clients || []).map(item => item.toLowerCase()).includes(term) ||
              (item.reference_id || '').toLowerCase().includes(term) ||
              (item.title || '').toLowerCase().includes(term)
          }
        },
        {
          value: 'appointment_future',
          label: 'Appointments',
          singleLabel: 'Appointment',
          component: markRaw(AppointmentFutureCard),
          permission: permissions.canAccessCalendar,
          minHeight: 76,
          tile: markRaw(AppointmentFutureTile),
          props: (partial) => {
            return {
              appointment: partial,
              ...getConfigForType('appointment')
            }
          },
          searchHandler: (item, term) => {
            return (item.address || '').toLowerCase().includes(term) ||
              (item.clients || []).map(item => item.toLowerCase()).includes(term) ||
              (item.reference_id || '').toLowerCase().includes(term) ||
              (item.title || '').toLowerCase().includes(term)
          }
        },
        {
          value: 'report',
          label: 'Reports',
          singleLabel: 'Report',
          component: markRaw(ReportCard),
          permission: permissions.canAccessReports,
          minHeight: 86,
          tile: markRaw(ReportTile),
          props: (partial) => {
            return {
              report: partial,
              ...getConfigForType('report')
            }
          },
          searchHandler: (item, term) => {
            return (item.template || '').toLowerCase().includes(term) ||
              (item.address || '').toLowerCase().includes(term) ||
              (item.client || '').toLowerCase().includes(term) ||
              (item.reference_id || '').toLowerCase().includes(term)
          }
        },
        {
          value: 'invoice',
          label: 'Invoices',
          singleLabel: 'Invoice',
          component: markRaw(InvoiceCard),
          permission: permissions.canAccessInvoicing,
          minHeight: 68,
          tile: markRaw(InvoiceTile),
          props: (partial) => {
            return {
              invoice: partial,
              ...getConfigForType('invoice')
            }
          },
          searchHandler: (item, term) => {
            return (item.address || '').toLowerCase().includes(term) ||
              (item.client || '').toLowerCase().includes(term) ||
              (item.invoice_number || '').toLowerCase().includes(term)
          }
        },
        {
          value: 'quotation',
          label: 'Quotations',
          singleLabel: 'Quotation',
          component: markRaw(QuotationCard),
          permission: permissions.canAccessQuotations,
          minHeight: 68,
          tile: markRaw(QuotationTile),
          props: (partial) => {
            return {
              quotation: partial,
              ...getConfigForType('quotation')
            }
          },
          searchHandler: (item, term) => {
            return (item.address || '').toLowerCase().includes(term) ||
              (item.client || '').toLowerCase().includes(term) ||
              (item.quotation_number || '').toLowerCase().includes(term)
          }
        },
        {
          value: 'drawing',
          label: 'Drawings',
          singleLabel: 'Drawing',
          component: markRaw(DrawingCard),
          permission: permissions.canAccessDrawings,
          minHeight: 68,
          tile: markRaw(DrawingTile),
          props: (partial) => {
            return {
              drawing: partial,
              ...getConfigForType('drawing')
            }
          },
          searchHandler: (item, term) => {
            return (item.title || '').toLowerCase().includes(term) ||
              (item.reference_id || '').toLowerCase().includes(term)
          }
        }
      ]
    },
    // Request saveHandler if an entry is updated
    baseSaveEntry (partialTypes, obj) {
      const type = partialTypes.find(item => item.value === obj.timeline_type)
      if (type && type.saveHandler) {
        type.saveHandler(obj)
      }
    },
    // Request pinHandler if an entry is pinned/unpinned
    basePinEntry (partialTypes, obj) {
      const type = partialTypes.find(item => item.value === obj.timeline_type)
      if (type && type.pinHandler) {
        type.pinHandler(obj, (isPinned = false) => {
          this.$q.notify({
            color: 'green',
            message: `Item ${isPinned ? 'pinned' : 'unpinned'}`
          })
        })
      }
    },
    // Request removeHandler if an entry is removed from the timeline
    baseRemoveEntry (partialTypes, timeline, obj, silent = false) {
      timeline.splice(timeline.indexOf(obj), 1)

      if (!silent) {
        const type = partialTypes.find(item => item.value === obj.timeline_type)
        if (type && type.removeHandler) {
          type.removeHandler(obj)
        }
      }
    }
  }
}
